<template>
  <div class="page-content pb-0">
    <Titlebar incrustation="fa-bullhorn">
      <h1>{{ $t("remoteMonitoring.list.title")}}</h1>
    </Titlebar>
    <UiCard>
      <SuperTable
        v-if="canBrowse"
        ref="tableProcedures"
        :tableColumns="tableColumns"
        :tableData="viewState === 'SUCCESS' ? rmListData.data : []"
        :loading="viewState === 'INITIAL' || viewState === 'LOADING'"
        @sort="handleSort"
        @filter="handleFilter"
        @delete="handleDelete"
      />
      <p v-if="!canBrowse">
        {{ $t('module.forbidden') }}
      </p>
    </UiCard>
    <Toolbar class="d-flex align-items-center">
      <div class="flex-grow-1">
        <UiButton2 @click.native.stop="handleLogs()" v-can="'beepiz-log-remotemonitoring'" class="ml-auto btn-lg">
          <i class="fa fa-list-ul mr-2"></i> {{ $t('remoteMonitoring.btns.logs') }}
        </UiButton2>
      </div>
      <div class="flex-grow-1">
        <TablePagination 
          ref="monitorpagination" 
          @change="handlePagination" 
        />
      </div>
      <div class="flex-grow-1 text-right">
        <UiButton2 
          v-can="'beepiz-add-remotemonitoring'"
          @click.native.stop="handleNew()" 
          class="btn-brand btn-lg ml-auto" 
        >
          <i class="fa fa-plus mr-2"></i> {{ $t('global.add') }}
        </UiButton2>
      </div>
    </Toolbar>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/services/API.js";
import Utils from "@/services/utils.js";
import Titlebar from "@/components/ui/Titlebar";
import UiCard from "@/components/ui/Card";
import Toolbar from "@/components/ui/Toolbar";
import NameTableRenderer from "@/components/monitoring/table-renderer/Name";
import StatusTableRenderer from "@/components/monitoring/table-renderer/Status";
import DateRenderer from "@/components/ui/table-renderer/DateRenderer";
import SuperTable from "@/components/ui/Table";
import TablePagination from "@/components/ui/Pagination";
import Globals from "@/services/globals.js";
import UiButton2 from '@/components/ui/Button2';
import {useDvFlag, useDvFlagAccess} from "../../services/featureFlag";

export default {
  name: "RemoteMonitoringList",
  components: {
    Titlebar,
    Toolbar,
    UiCard,
    SuperTable,
    NameTableRenderer,
    StatusTableRenderer,
    TablePagination,
    UiButton2,
    DateRenderer,
  },
  props: {},
  data() {
    return {
      viewState: 'INITIAL', // INITIAL, LOADING, ERROR, SUCCESS
      rmListData: null,
      tableColumns: {
        global: {
          sortable: false,
          noItemsMsg: window.V.$i18n.t(
            "remoteMonitoring.procedures.emptyTable"
          ),
        },
        columns: [
          {
            key: "name",
            label: Utils.t("remoteMonitoring.procedures.columns.name"),
            showPopup: false,
            activeFilter: "",
            activeSort: '+',
            sortable: true,
            filter: {
              type: "text",
            },
            event: "edit",
            renderer: NameTableRenderer,
            with: "50%",
          },
          {
            key: "status",
            label: Utils.t("remoteMonitoring.procedures.columns.status"),
            sortable: true,
            // width: "10%",
            showPopup: false,
            activeFilter: "",
            activeSort: false,
            filter: false,
            // filter: {
            //   type: 'list',
            //   values: [
            //     {
            //       id: 'verified',
            //       label: window.V.$i18n.t('remoteMonitoring.validation.verified')
            //     },
            //     {
            //       id:'waiting',
            //       label: window.V.$i18n.t('remoteMonitoring.validation.waiting')
            //     },
            //     {
            //       id: 'refused',
            //       label: window.V.$i18n.t('remoteMonitoring.validation.refused')
            //     },
            //   ]
            // },
            renderer: StatusTableRenderer
          },
          {
            key: "watchmen_channels_count",
            label: Utils.t("remoteMonitoring.procedures.columns.count"),
            showPopup: false,
            activeFilter: "",
            activeSort: false,
            sortable: true,
            filter: false,
            className: "",
            renderer: (item) => {
              return item.watchmen_channels_count > 0 ? `
                <span class="renderer text-mono">
                  <strong>${item.watchmen_channels_count}</strong>
                </span>
              ` : `
                <span class="renderer text-mono">
                  <span class="text-danger">0</span>
                </span>
              `
            },
          },
          {
            key: "datis_count",
            label: Utils.t("remoteMonitoring.procedures.columns.countDati"),
            showPopup: false,
            activeFilter: "",
            activeSort: false,
            sortable: true,
            filter: false,
            className: "",
            renderer: (item) => {
              return item.datis_count > 0 ? `
                <span class="renderer text-mono">
                  <strong>${item.datis_count}</strong>
                </span>
              ` : `
                <span class="renderer text-mono">
                  <span class="text-gray-400">0</span>
                </span>
              `
            },
            // width: "20%",
          },
          {
            key: "updated_at",
            label: Utils.t("remoteMonitoring.procedures.columns.date"),
            showPopup: false,
            activeFilter: "",
            activeSort: false,
            sortable: true,
            filter: false,
            className: "",
            renderer: DateRenderer,
            // width: "20%",
          },
        ],
        rowActions: {
          // width: "15%",
          actions: [
            {
              label: Utils.t("global.delete"),
              class: "fa fa-trash",
              event: "delete",
            },
          ],
        },
      },
      config: {
        perpage: Globals.perPage,
        fields: [],
        page: 1,
        filter: {
          like: [],
          equals: [],
        },
        sort: ["name"],
      },
    };
  },
  computed: {
    canBrowse() {
      return useDvFlagAccess("beepiz-remote-monitoring-can:read");
    },
  },
  mounted() {
    const flag = useDvFlag('beepiz-remote-monitoring-can:read');
    if (!flag.access && flag.fallback==="no_option") {
      this.$router.push({ name: 'AccessDenied', params: { slug: "remotemonitoring"} })
    }
    if (!flag.access && flag.fallback==="no_role") {
      this.$router.push({ name: 'AccessDenied', params: { slug: "remotemonitoring_no_role"} })
    }
  },
  methods: {
    getRmList() {
      this.viewState = 'LOADING';
      axios
        .get(
          API.route(API.endpoints.remoteMonitoring.index) +
            API.constructQueryParams(this.config)
        )
        .then((response) => {
          const { data } = response;
          data.data.forEach(procedure => {
            if (procedure.status === 'waiting') {
              procedure.spRowClassname = "no-delete-action"
            }
          });
          this.rmListData = data;
          this.viewState = 'SUCCESS';
        })                                               
        .catch((error) => {
          this.viewState = 'ERROR';
        });
    },
    handlePagination: function (page) {
      this.config.page = page;
      this.getRmList();
    },
    handleSort: function () {
      this.config.sort = [];
      for (var colKey in this.tableColumns.columns) {
        var col = this.tableColumns.columns[colKey];
        if (col.activeSort) {
          this.config.sort.push(col.activeSort + col.key);
        }
      }
      this.getRmList();
    },
    handleFilter: function () {
      this.config.page = 1;
      this.config.filter.like = [];
      this.config.filter.equals = [];
      for (var colKey in this.tableColumns.columns) {
        var col = this.tableColumns.columns[colKey];
        if (col.activeFilter != "") {
          if (col.filter && col.filter.type == "text") {
            this.config.filter.like.push(col.key + ":" + col.activeFilter);
          }
          if (col.filter && col.filter.type == "list") {
            this.config.filter.equals.push(col.key + ":" + col.activeFilter);
          }
        }
      }
      this.getRmList();
    },
    handleDelete: function (item) {
      let modal_content = `<p>${Utils.t("remoteMonitoring.delete_modal_content")}</p>`;
      if (item.datis_count > 0) {
        modal_content += `<p>${Utils.t("remoteMonitoring.delete_modal_contains_datis")}</p>`;
      }

      window.Vfc.$refs.confirm.show({
        title: Utils.t("global.deleteModalTitle"),
        content: modal_content,
        callbackYes: () => {
          this.viewState = 'LOADING';
          axios
            .delete(
              API.route(API.endpoints.remoteMonitoring.delete, {
                procedureId: item.id,
              })
            )
            .then((response) => {
              window.Vfc.$refs.messages.sendMessage(
                "success",
                Utils.t("remoteMonitoring.procedures.deleteSuccess")
              );
              if (this.rmListData.data.length == 1 && this.config.page > 1) {
                this.config.page--;
              }
              this.getRmList();
            })
            .catch((error) => {
              this.viewState = 'ERROR';
              this.i18nErrorMessage(`remoteMonitoring.back_end_messages.${error?.response?.data?.message}`)
            });
        },
        yesButtonText: Utils.tc("global.delete"),
        noButtonText: Utils.tc("global.cancel"),
      });
    },
    handleNew() {
      this.$router.push({
        name: 'RemoteMonitoringNew'
      });
    },
    handleLogs() {
      this.$router.push({
        name: 'RemoteMonitoringLogs'
      });
    },
    i18nErrorMessage(i18nErrorPath) {
      window.Vfc.$refs.messages.sendMessage(
        'error',
        this.$te(i18nErrorPath)
          ? this.$t(i18nErrorPath)
          : this.$t('errors.default')
      )
    },
  },
  created() {
    if (!useDvFlagAccess("beepiz-remote-monitoring-can:read")) {
      this.$router.push({ name: 'AccessDenied', params: { slug: 'remotemonitoring'} })
    } else {
      this.getRmList();
      window.Vfc.$refs.loading.hide();
    }
  },
  watch: {
    rmListData: {
      handler: function (val) {
        this.$refs.monitorpagination.update(val.meta.pagination);
      },
      deep: true,
    },
  }
};
</script>
